import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import AuthGeneralInput from "../../components/input/authinput";
import Modal from "../../components/modal/modal";
import SubmissionService from "../../services/submissionService";
import CompetitionService from "../../services/competitionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faFilter, faCheckCircle, faTimesCircle, faArrowUp, faVideo } from "@fortawesome/free-solid-svg-icons";

const STATUS_COLORS = {
  draft: 'bg-grey-200 text-grey-667',
  submitted: 'bg-primary-bg text-primary',
  under_review: 'bg-yellow-500/10 text-orange-dark',
  advanced: 'bg-green-light text-green',
  eliminated: 'bg-red-500/10 text-red-500',
};

const NEXT_ROUND = { round1: 'round2', round2: 'round3', round3: 'round4', round4: 'round5', round5: 'round6', round6: 'implementation' };

function VideoCell({ videoPitch }) {
  if (!videoPitch?.url) return <span className="text-xs text-grey-98">—</span>;
  const tooltip = videoPitch.submittedAt
    ? `Submitted ${new Date(videoPitch.submittedAt).toLocaleString()}`
    : 'Video pitch submitted';
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold bg-green-light text-green" title={tooltip}>
      <FontAwesomeIcon icon={faVideo} /> Video
    </span>
  );
}

function ScoreCell({ scoring, round }) {
  const avg = scoring?.[round];
  if (!avg) return <span className="text-xs text-grey-98">—</span>;
  const meets = avg.averageMeetsThreshold;
  const bg = meets ? 'bg-green-light text-green' : 'bg-red-500/10 text-red-500';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${bg}`} title={`${avg.judgeCount} judge${avg.judgeCount === 1 ? '' : 's'} — H:${avg.humanitarianImpact} F:${avg.feasibility} I:${avg.innovation} C:${avg.clarityOfPlan}`}>
      {avg.total.toFixed(1)}/20
    </span>
  );
}

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [competitionId, setCompetitionId] = useState(null);
  const [filters, setFilters] = useState({ status: '', currentRound: '', hasVideo: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchAction, setBatchAction] = useState(null); // 'advance' | 'eliminate' | null
  const [batchForm, setBatchForm] = useState({ toRound: '', notifyEnabled: true, title: '', body: '' });
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  useEffect(() => { loadCompetition(); }, []);
  useEffect(() => { if (competitionId) loadSubmissions(); }, [competitionId, filters]);

  const loadCompetition = async () => {
    try {
      const { data } = await CompetitionService.getActive();
      setCompetitionId(data.data.competition._id);
    } catch {
      ToastError('No active competition found');
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const params = { competitionId, includeScores: 'true', ...filters };
      const { data } = await SubmissionService.getAll(params);
      setSubmissions(data.data.submissions || []);
      setPagination({ page: data.data.page, pages: data.data.pages, total: data.data.total });
      setSelectedIds(new Set()); // clear selection when filter changes
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load submissions');
    } finally { setLoading(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(submissions.map(s => s._id)));
  };

  const selectedSubmissions = useMemo(
    () => submissions.filter(s => selectedIds.has(s._id)),
    [submissions, selectedIds]
  );

  const openBatchModal = (action) => {
    setBatchAction(action);
    // Default round = next round of the most common currentRound in selection
    const rounds = selectedSubmissions.map(s => s.currentRound);
    const defaultFrom = rounds[0];
    const defaultTo = NEXT_ROUND[defaultFrom] || 'round2';
    setBatchForm({
      toRound: defaultTo,
      notifyEnabled: true,
      title: action === 'advance' ? "You've advanced to the next round" : "Update on your submission",
      body: action === 'advance'
        ? `Congratulations! Your submission has advanced. Please log in to your portal for next steps.`
        : `Thank you for your submission. After careful review, your submission was not selected to advance. We appreciate your participation.`
    });
  };

  const handleBatchSubmit = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBatchSubmitting(true);
    try {
      const notification = batchForm.notifyEnabled && batchForm.title.trim()
        ? { title: batchForm.title.trim(), body: batchForm.body.trim() }
        : null;

      if (batchAction === 'advance') {
        const { data } = await SubmissionService.bulkAdvance({
          submissionIds: ids, toRound: batchForm.toRound, notification
        });
        Success(data.message || `${ids.length} advanced`);
      } else {
        const { data } = await SubmissionService.bulkEliminate({
          submissionIds: ids, notification
        });
        Success(data.message || `${ids.length} eliminated`);
      }
      setBatchAction(null);
      loadSubmissions();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Batch action failed');
    } finally { setBatchSubmitting(false); }
  };

  const allSelected = submissions.length > 0 && selectedIds.size === submissions.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <H1Title size="sm">All Submissions</H1Title>
        <span className="text-sm text-grey-667">{pagination.total} total</span>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <FontAwesomeIcon icon={faFilter} className="text-grey-98" />
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="advanced">Advanced</option>
          <option value="eliminated">Eliminated</option>
        </select>
        <select
          value={filters.currentRound}
          onChange={(e) => setFilters({ ...filters, currentRound: e.target.value })}
          className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="">All Rounds</option>
          <option value="round1">Round 1</option>
          <option value="round2">Round 2</option>
          <option value="round3">Round 3</option>
          <option value="round4">Round 4</option>
          <option value="round5">Round 5</option>
          <option value="round6">Round 6</option>
        </select>
        <select
          value={filters.hasVideo}
          onChange={(e) => setFilters({ ...filters, hasVideo: e.target.value })}
          className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="">All Videos</option>
          <option value="true">Video Submitted</option>
          <option value="false">No Video</option>
        </select>
      </div>

      {/* Batch action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-primary-bg border border-primary/20 rounded-lg px-4 py-3 flex items-center justify-between sticky top-16 z-10">
          <p className="text-sm font-semibold text-primary">{selectedIds.size} selected</p>
          <div className="flex gap-2">
            <AppButton onClick={() => openBatchModal('advance')} size="sm" fullWidth={false} className="px-4">
              <FontAwesomeIcon icon={faArrowUp} className="mr-1" /> Advance Selected
            </AppButton>
            <AppButton onClick={() => openBatchModal('eliminate')} variant="danger" size="sm" fullWidth={false} className="px-4">
              <FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Eliminate Selected
            </AppButton>
            <AppButton onClick={() => setSelectedIds(new Set())} variant="outline" size="sm" fullWidth={false} className="px-4">
              Clear
            </AppButton>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-grey-667">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="p-12 text-center text-grey-667">No submissions found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-grey-bg border-b border-grey-200">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="cursor-pointer" />
                </th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Submitter</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Team</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Round</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Video</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">R1</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">R2</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Composite</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Updated</th>
                <th className="text-left px-4 py-3 font-semibold text-grey-dark">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {submissions.map((sub) => {
                const checked = selectedIds.has(sub._id);
                return (
                  <tr key={sub._id} className={`hover:bg-grey-bg/50 ${checked ? 'bg-primary-bg/20' : ''}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={checked} onChange={() => toggleSelect(sub._id)} className="cursor-pointer" />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold">{sub.submitterId?.firstName} {sub.submitterId?.lastName}</p>
                      <p className="text-xs text-grey-98">{sub.submitterId?.email}</p>
                    </td>
                    <td className="px-4 py-3 text-grey-667">{sub.teamId?.name || 'Individual'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[sub.status] || STATUS_COLORS.draft}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-grey-667">{sub.currentRound}</td>
                    <td className="px-4 py-3"><VideoCell videoPitch={sub.videoPitch} /></td>
                    <td className="px-4 py-3"><ScoreCell scoring={sub.scoring} round="round1" /></td>
                    <td className="px-4 py-3"><ScoreCell scoring={sub.scoring} round="round2" /></td>
                    <td className="px-4 py-3 text-grey-667 font-mono text-xs">
                      {sub.compositeScore != null ? sub.compositeScore.toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-grey-98 text-xs">
                      {sub.updatedAt ? new Date(sub.updatedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/submissions/${sub._id}`} className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                        <FontAwesomeIcon icon={faEye} /> View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Batch Modal */}
      {batchAction && (
        <Modal handleClose={() => setBatchAction(null)} title={batchAction === 'advance' ? `Advance ${selectedIds.size} Submission${selectedIds.size === 1 ? '' : 's'}` : `Eliminate ${selectedIds.size} Submission${selectedIds.size === 1 ? '' : 's'}`}>
        <div className="space-y-4">
          <div className="bg-grey-bg rounded-lg p-3 max-h-40 overflow-y-auto">
            <Paragraph size="sm" className="text-grey-667 mb-2">Selected:</Paragraph>
            <ul className="text-xs text-grey-dark space-y-1">
              {selectedSubmissions.map(s => (
                <li key={s._id}>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary mr-1 text-[10px]" />
                  {s.submitterId?.firstName} {s.submitterId?.lastName} — {s.teamId?.name || 'Individual'} ({s.currentRound})
                </li>
              ))}
            </ul>
          </div>

          {batchAction === 'advance' && (
            <div>
              <label className="block text-sm font-semibold mb-1">Advance to:</label>
              <select
                value={batchForm.toRound}
                onChange={(e) => setBatchForm({ ...batchForm, toRound: e.target.value })}
                className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none"
              >
                <option value="round2">Round 2 — Recorded Pitch</option>
                <option value="round3">Round 3 — Finalist Interviews</option>
                <option value="round4">Round 4 — Meet the Team</option>
                <option value="round5">Round 5 — Fundraising & Logistics</option>
                <option value="round6">Round 6 — Implementation</option>
                <option value="implementation">Implementation Phase</option>
              </select>
            </div>
          )}

          <div className="border-t border-grey-200 pt-4">
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={batchForm.notifyEnabled}
                onChange={(e) => setBatchForm({ ...batchForm, notifyEnabled: e.target.checked })}
              />
              <span className="text-sm font-semibold">Send notification to recipients</span>
            </label>

            {batchForm.notifyEnabled && (
              <div className="space-y-3">
                <AuthGeneralInput
                  label="Notification Title"
                  name="title"
                  placeholder="e.g. You've advanced to Round 2!"
                  value={batchForm.title}
                  change={(e) => setBatchForm({ ...batchForm, title: e.target.value })}
                />
                <div>
                  <label className="block text-xs md:text-sm mb-2 text-black">Message Body</label>
                  <textarea
                    className="w-full border border-grey-200 rounded-md p-3 text-sm min-h-[100px] outline-none focus:border-primary resize-y"
                    value={batchForm.body}
                    onChange={(e) => setBatchForm({ ...batchForm, body: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <AppButton onClick={() => setBatchAction(null)} variant="outline" size="sm" fullWidth={false} className="px-4">Cancel</AppButton>
            <AppButton
              onClick={handleBatchSubmit}
              loading={batchSubmitting}
              variant={batchAction === 'eliminate' ? 'danger' : 'primary'}
              size="sm"
              fullWidth={false}
              className="px-4"
            >
              {batchAction === 'advance' ? 'Advance' : 'Eliminate'}
            </AppButton>
          </div>
        </div>
        </Modal>
      )}
    </div>
  );
}
